import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { shortDay, when } from "@/pages/developers/console/range";
import { useDisplayMoney } from "@/pages/developers/console/shared";

const STORE_NAMES = {
  ebay: "eBay",
  amazon: "Amazon",
  walmart: "Walmart",
  etsy: "Etsy",
  target: "Target",
  poshmark: "Poshmark",
};

export function clickLabel(row) {
  const title = String(row?.title || "").trim();
  const id = String(row?.listing_id || "");
  const raw = title || id;
  const looksLikeId = !title || title.includes("~") || /^[a-z0-9]+:/i.test(title);
  if (!looksLikeId) return title;
  if (raw.startsWith("in:")) {
    const platform = raw.split(":")[1];
    return platform ? `${platform} product` : "India store product";
  }
  const store = (raw.split(":")[0] || "").toLowerCase();
  if (STORE_NAMES[store]) return `${STORE_NAMES[store]} product`;
  return "Product";
}

export function compactCount(n) {
  const v = Number(n) || 0;
  if (v >= 1000000) return `${(v / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return v.toLocaleString();
}

function MiniChart({ data, dataKey, color, money, label }) {
  const show = useDisplayMoney();
  return (
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
          <CartesianGrid stroke="#27272A" vertical={false} />
          <XAxis dataKey="date" tickFormatter={shortDay} tick={{ fill: "#A1A1AA", fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={28} />
          <YAxis allowDecimals={!!money} tick={{ fill: "#A1A1AA", fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
          <Tooltip
            cursor={{ stroke: "#27272A" }}
            contentStyle={{ background: "#121214", border: "1px solid #27272A", fontSize: 12 }}
            labelFormatter={shortDay}
            formatter={(v) => [money ? show(v, "USD") : v, label]}
          />
          <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} fill={color} fillOpacity={0.12} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function TrafficOverview({ totals = {}, allTime = {}, series = [], activity = [], usage, rangeLabel, tz }) {
  const show = useDisplayMoney();
  const earn = Number(totals.estimated_commission || 0);
  return (
    <div data-testid="traffic-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="border border-aura-border p-4" data-testid="stat-all-time">
          <div className="text-[11px] tracking-luxe uppercase text-aura-muted">All-time earnings</div>
          <div className="font-serif text-3xl mt-2">{show(allTime.estimated_commission || 0, "USD")}</div>
        </div>
        <div className="border border-aura-border p-4" data-testid="stat-earnings">
          <div className="text-[11px] tracking-luxe uppercase text-aura-muted">Earnings · {rangeLabel}</div>
          <div className="font-serif text-3xl mt-2">{show(earn, "USD")}</div>
        </div>
        <div className="border border-aura-border p-4" data-testid="stat-impressions">
          <div className="text-[11px] tracking-luxe uppercase text-aura-muted">Views · {rangeLabel}</div>
          <div className="font-serif text-3xl mt-2">{compactCount(totals.impressions)}</div>
        </div>
        <div className="border border-aura-border p-4" data-testid="stat-clicks">
          <div className="text-[11px] tracking-luxe uppercase text-aura-muted">Clicks · {rangeLabel}</div>
          <div className="font-serif text-3xl mt-2">{compactCount(totals.clicks)}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-3 mb-6">
        <section className="border border-aura-border p-4" data-testid="chart-earnings">
          <div className="text-sm mb-3">Earnings</div>
          {earn === 0 && series.every((p) => !p.estimated_commission) ? (
            <div className="h-48 flex items-center justify-center text-sm text-aura-muted">No activity in this range</div>
          ) : (
            <MiniChart data={series} dataKey="estimated_commission" color="#4CC9F0" money label="Earnings" />
          )}
        </section>
        <section className="border border-aura-border p-4" data-testid="chart-clicks">
          <div className="text-sm mb-3">Clicks</div>
          <MiniChart data={series} dataKey="clicks" color="#7C6BF2" label="Clicks" />
        </section>
      </div>

      {usage && (
        <section className="border border-aura-border p-4 mb-6" data-testid="chart-usage">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm">API usage</div>
            <div className="text-xs text-aura-muted">{compactCount(usage.total)} requests</div>
          </div>
          <MiniChart data={usage.series || []} dataKey="requests" color="#4CC9F0" label="Requests" />
        </section>
      )}

      <section className="border border-aura-border mb-10" data-testid="latest-activity">
        <div className="px-4 py-3 text-sm border-b border-aura-border">Latest activity</div>
        {activity.length === 0 ? (
          <div className="px-4 py-6 text-sm text-aura-muted">No clicks in this range.</div>
        ) : (
          <ul>
            {activity.slice(0, 6).map((row, i) => (
              <li key={`${row.at}-${i}`} className="px-4 py-3 border-b border-aura-border last:border-0">
                <div className="text-sm">{clickLabel(row)}</div>
                <div className="text-xs text-aura-muted mt-0.5">Click · {when(row.at, tz)}</div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
